import { useState } from "react";
import classes from "./EventForm.module.css";
import { getEventStatus } from "../../utils/eventStatus";

const emptyEvent = {
    title: "",
    date: "",
    time: "",
    location: "",
    description: "",
    imageUrl: "",
    videoUrl: "",
    registrationUrl: "",
    resultsUrl: "",
    mapUrl: "",
};

function slugify(title) {
    return title
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)/g, "");
}

function formatDisplayDate(date) {
    return new Date(date).toLocaleDateString("en-IE", {
        year: "numeric",
        month: "long",
        day: "numeric",
    });
}

function toEmbedUrl(url) {
    const watchMatch = url.match(/youtube\.com\/watch\?v=([\w-]+)/);
    if (watchMatch) return `https://www.youtube.com/embed/${watchMatch[1]}`;
    const shortMatch = url.match(/youtu\.be\/([\w-]+)/);
    if (shortMatch) return `https://www.youtube.com/embed/${shortMatch[1]}`;
    return url;
}

export default function EventForm({ initialEvent, token, onSubmit, onCancel, saving }) {
    const [form, setForm] = useState(initialEvent || emptyEvent);
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState("");

    const handleChange = (field) => (e) => {
        setForm((prev) => ({ ...prev, [field]: e.target.value }));
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        e.target.value = ""; // BS: reset so re-selecting the same file still fires onChange
        if (!file) return;

        setUploadError("");
        setUploading(true);
        try {
            const signRes = await fetch("/api/cloudinary-sign", {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!signRes.ok) throw new Error("Could not authorise upload");
            const { signature, timestamp, apiKey, cloudName, folder } = await signRes.json();

            const data = new FormData();
            data.append("file", file);
            data.append("api_key", apiKey);
            data.append("timestamp", timestamp);
            data.append("folder", folder);
            data.append("signature", signature);

            const uploadRes = await fetch(
                `https://api.cloudinary.com/v1_1/${cloudName}/image/upload`,
                { method: "POST", body: data }
            );
            if (!uploadRes.ok) throw new Error("Upload failed");
            const uploaded = await uploadRes.json();

            setForm((prev) => ({ ...prev, imageUrl: uploaded.secure_url }));
        } catch (err) {
            setUploadError(err.message || "Upload failed");
        } finally {
            setUploading(false);
        }
    };

    const handleRemoveImage = () => {
        setForm((prev) => ({ ...prev, imageUrl: "" }));
    };

    const handleVideoBlur = () => {
        if (!form.videoUrl) return;
        setForm((prev) => ({ ...prev, videoUrl: toEmbedUrl(prev.videoUrl) }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const id = initialEvent ? initialEvent.id : `${slugify(form.title)}-${form.date}`;
        const status = getEventStatus(form.date, form.time);
        onSubmit({ ...form, id, status });
    };

    return (
        <form className={classes.form} onSubmit={handleSubmit}>
            <h2 className={classes.title}>{initialEvent ? "Edit event" : "Add new event"}</h2>

            <label className={classes.label}>
                Title
                <input className={classes.input} value={form.title} onChange={handleChange("title")} required />
            </label>

            <label className={classes.label}>
                Date
                <input className={classes.input} type="date" value={form.date} onChange={handleChange("date")} required />
            </label>
            {form.date && (
                <p className={classes.statusHint}>{formatDisplayDate(form.date)}</p>
            )}

            <label className={classes.label}>
                Time
                <input className={classes.input} type="time" lang="en-IE" value={form.time} onChange={handleChange("time")} />
            </label>

            <label className={classes.label}>
                Location
                <input className={classes.input} value={form.location} onChange={handleChange("location")} />
            </label>

            <label className={classes.label}>
                Google Maps link (paste the share link — users will see the location text above, linked to this map)
                <input className={classes.input} value={form.mapUrl} onChange={handleChange("mapUrl")} placeholder="https://maps.app.goo.gl/..." />
            </label>

            <label className={classes.label}>
                Registration link
                <input className={classes.input} value={form.registrationUrl} onChange={handleChange("registrationUrl")} placeholder="https://..." />
            </label>

            <label className={classes.label}>
                Results link (shown as a RESULTS button once the event is in the past)
                <input className={classes.input} value={form.resultsUrl} onChange={handleChange("resultsUrl")} placeholder="https://..." />
            </label>

            <label className={classes.label}>
                Description
                <textarea className={classes.textarea} value={form.description} onChange={handleChange("description")} rows={4} />
            </label>

            <div className={classes.label}>
                <span>Image</span>
                <input className={classes.input} value={form.imageUrl} onChange={handleChange("imageUrl")} placeholder="Upload a file or paste an image URL" />

                {form.imageUrl ? (
                    <div className={classes.previewWrap}>
                        <div className={classes.previewCard}>
                            <img className={classes.preview} src={form.imageUrl} alt="Event preview" />
                            <button
                                type="button"
                                className={classes.removeImage}
                                onClick={handleRemoveImage}
                                disabled={saving}
                                aria-label="Remove image"
                            >
                                ×
                            </button>
                        </div>
                        <label className={classes.replaceButton}>
                            {uploading ? "Uploading…" : "Replace image"}
                            <input type="file" accept="image/*" onChange={handleImageUpload} disabled={uploading || saving} hidden />
                        </label>
                    </div>
                ) : (
                    <label className={`${classes.dropZone} ${uploading ? classes.dropZoneBusy : ""}`}>
                        <svg
                            className={classes.uploadIcon}
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden="true"
                        >
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                            <polyline points="17 8 12 3 7 8" />
                            <line x1="12" y1="3" x2="12" y2="15" />
                        </svg>
                        <span className={classes.dropText}>{uploading ? "Uploading…" : "Click to upload from device"}</span>
                        <span className={classes.dropHint}>PNG or JPG — or paste a URL above</span>
                        <input type="file" accept="image/*" onChange={handleImageUpload} disabled={uploading || saving} hidden />
                    </label>
                )}

                {uploadError && <span className={classes.uploadError}>{uploadError}</span>}
            </div>

            <label className={classes.label}>
                Video URL (paste a YouTube link, it's converted automatically)
                <input className={classes.input} value={form.videoUrl} onChange={handleChange("videoUrl")} onBlur={handleVideoBlur} />
            </label>

            <p className={classes.statusHint}>
                Status: <strong>{getEventStatus(form.date, form.time) === "past" ? "Past" : "Upcoming"}</strong> (set automatically from the date)
            </p>

            <div className={classes.actions}>
                <button className={classes.cancelButton} type="button" onClick={onCancel} disabled={saving}>
                    Cancel
                </button>
                <button className={classes.saveButton} type="submit" disabled={saving}>
                    {saving ? "Saving..." : "Save"}
                </button>
            </div>
        </form>
    );
}
